import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  LinearProgress,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import GrainIcon from '@mui/icons-material/Grain';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import GrassIcon from '@mui/icons-material/Grass';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { recipeApi } from '../services/api';

// Nutrient stat box
const NutrientStat = ({ label, value, unit, icon, color }) => (
  <Box sx={{ textAlign: 'center', p: 1 }}>
    {icon}
    <Typography variant="h6" fontWeight={700} color={color || 'text.primary'}>
      {value != null ? Math.round(value) : '-'}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {unit ? `${label} (${unit})` : label}
    </Typography>
  </Box>
);

// RDA percentage bar
const RdaBar = ({ label, percentage }) => (
  <Box sx={{ mb: 1.5 }}>
    <Box display="flex" justifyContent="space-between" mb={0.5}>
      <Typography variant="caption" fontWeight={500}>
        {label.replace(/_/g, ' ')}
      </Typography>
      <Typography variant="caption" fontWeight={600} color={percentage >= 50 ? 'success.main' : 'text.secondary'}>
        {percentage}%
      </Typography>
    </Box>
    <LinearProgress
      variant="determinate"
      value={Math.min(percentage, 100)}
      sx={{
        height: 6,
        borderRadius: 3,
        bgcolor: 'grey.200',
        '& .MuiLinearProgress-bar': {
          borderRadius: 3,
          bgcolor: percentage >= 75 ? 'success.main' : percentage >= 50 ? 'info.main' : percentage >= 25 ? 'warning.main' : 'grey.400',
        },
      }}
    />
  </Box>
);

function RecipeDetailModal({ open, onClose, recipeId }) {
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && recipeId) {
      setLoading(true);
      setError(null);
      recipeApi.get(recipeId)
        .then((data) => {
          setRecipe(data);
        })
        .catch((err) => {
          console.error('Error fetching recipe:', err);
          setError('Failed to load recipe details.');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setRecipe(null);
    }
  }, [open, recipeId]);

  // Nutrition values are top-level fields, not nested under a nutrition object
  const micronutrients = recipe?.micronutrients || {};
  const rda = recipe?.rda_percentages || {};

  // Format micronutrient display
  const microRows = Object.entries(micronutrients).filter(([, v]) => v != null && v > 0).map(([key, value]) => {
    const labels = {
      calcium_mg: 'Calcium (mg)',
      iron_mg: 'Iron (mg)',
      magnesium_mg: 'Magnesium (mg)',
      potassium_mg: 'Potassium (mg)',
      zinc_mg: 'Zinc (mg)',
      vitamin_c_mg: 'Vitamin C (mg)',
      vitamin_d_iu: 'Vitamin D (IU)',
      vitamin_b12_mcg: 'Vitamin B12 (mcg)',
      folate_mcg: 'Folate (mcg)',
      omega3_g: 'Omega-3 (g)',
    };
    return { key, label: labels[key] || key.replace(/_/g, ' '), value };
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        {loading ? 'Loading...' : recipe?.name || 'Recipe Details'}
        {recipe?.difficulty && (
          <Chip
            label={recipe.difficulty}
            size="small"
            color={recipe.difficulty === 'Easy' ? 'success' : recipe.difficulty === 'Medium' ? 'warning' : 'error'}
            sx={{ ml: 1, verticalAlign: 'middle' }}
          />
        )}
        {recipe?.total_time && (
          <Chip
            icon={<AccessTimeIcon />}
            label={`${recipe.total_time} min`}
            size="small"
            variant="outlined"
            sx={{ ml: 1, verticalAlign: 'middle' }}
          />
        )}
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {recipe && !loading && (
          <Box>
            {/* Description */}
            {recipe.description && (
              <Typography variant="body2" color="text.secondary" mb={2}>
                {recipe.description}
              </Typography>
            )}

            {/* Nutrition Highlight */}
            {recipe.nutrition_highlight && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600}>
                  {recipe.nutrition_highlight}
                </Typography>
              </Alert>
            )}

            {/* Quick Stats - nutrition values are top-level fields */}
            <Box display="flex" justifyContent="space-around" flexWrap="wrap" sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 1, mb: 2 }}>
              <NutrientStat label="Calories" value={recipe.calories} icon={<LocalFireDepartmentIcon sx={{ fontSize: 20, color: 'error.main' }} />} color="error.main" />
              <NutrientStat label="Protein" value={recipe.protein} unit="g" icon={<FitnessCenterIcon sx={{ fontSize: 20, color: 'info.main' }} />} color="info.main" />
              <NutrientStat label="Carbs" value={recipe.carbs} unit="g" icon={<GrainIcon sx={{ fontSize: 20, color: 'warning.main' }} />} color="warning.main" />
              <NutrientStat label="Fat" value={recipe.fat} unit="g" icon={<WaterDropIcon sx={{ fontSize: 20, color: 'secondary.main' }} />} color="secondary.main" />
              <NutrientStat label="Fiber" value={recipe.fiber} unit="g" icon={<GrassIcon sx={{ fontSize: 20, color: 'success.main' }} />} color="success.main" />
            </Box>

            {/* Prep/Cook info row */}
            <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
              {recipe.prep_time != null && (
                <Chip icon={<AccessTimeIcon />} label={`Prep: ${recipe.prep_time} min`} size="small" variant="outlined" />
              )}
              {recipe.cook_time != null && (
                <Chip icon={<AccessTimeIcon />} label={`Cook: ${recipe.cook_time} min`} size="small" variant="outlined" />
              )}
              {recipe.servings != null && (
                <Chip icon={<RestaurantIcon />} label={`${recipe.servings} servings`} size="small" variant="outlined" />
              )}
              {recipe.estimated_cost != null && (
                <Chip icon={<AttachMoneyIcon />} label={`$${recipe.estimated_cost.toFixed(2)}`} size="small" variant="outlined" />
              )}
            </Box>

            {/* Health Tags */}
            {(recipe.health_goals?.length > 0 || recipe.tags?.length > 0 || recipe.allergens?.length > 0 || recipe.nutrition_profile_tags?.length > 0) && (
              <Box mb={2}>
                {recipe.health_goals?.length > 0 && (
                  <Box mb={1}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary">Health Goals</Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                      {recipe.health_goals.map((g, i) => (
                        <Chip key={i} label={g.replace(/_/g, ' ')} size="small" color="success" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}
                {recipe.tags?.length > 0 && (
                  <Box mb={1}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary">Tags</Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                      {recipe.tags.map((t, i) => (
                        <Chip key={i} label={t.replace(/_/g, ' ')} size="small" color="primary" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}
                {recipe.nutrition_profile_tags?.length > 0 && (
                  <Box mb={1}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary">Nutrition Profile</Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                      {recipe.nutrition_profile_tags.map((t, i) => (
                        <Chip key={i} label={t.replace(/_/g, ' ')} size="small" color="info" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}
                {recipe.allergens?.length > 0 && (
                  <Box mb={1}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary">Allergens</Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                      {recipe.allergens.map((a, i) => (
                        <Chip key={i} label={a.replace(/_/g, ' ')} size="small" color="error" />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Ingredients & Instructions side by side */}
            <Grid container spacing={3}>
              {/* Ingredients */}
              {recipe.ingredients?.length > 0 && (
                <Grid item xs={12} md={5}>
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>
                    Ingredients
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {recipe.ingredients.map((ing, idx) => (
                      <li key={idx}>
                        <Typography variant="body2">{ing}</Typography>
                      </li>
                    ))}
                  </Box>
                </Grid>
              )}

              {/* Instructions */}
              {recipe.instructions?.length > 0 && (
                <Grid item xs={12} md={7}>
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>
                    Instructions
                  </Typography>
                  <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                    {recipe.instructions.map((step, idx) => (
                      <li key={idx} style={{ marginBottom: 8 }}>
                        <Typography variant="body2">{step}</Typography>
                      </li>
                    ))}
                  </Box>
                </Grid>
              )}
            </Grid>

            {/* Substitutions */}
            {recipe.substitutions?.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                  Substitutions
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {recipe.substitutions.map((sub, idx) => (
                    <li key={idx}>
                      <Typography variant="body2">
                        {typeof sub === 'string' ? sub : `${sub.original} → ${sub.substitute}${sub.note ? ` (${sub.note})` : ''}`}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </>
            )}

            {/* Micronutrients */}
            {microRows.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                  Micronutrients
                </Typography>
                <Grid container spacing={1}>
                  {microRows.map(({ key, label, value }) => (
                    <Grid item xs={6} sm={4} key={key}>
                      <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 1, textAlign: 'center' }}>
                        <Typography variant="body2" fontWeight={600}>{typeof value === 'number' ? value.toFixed(1) : value}</Typography>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

            {/* RDA Percentages */}
            {Object.keys(rda).length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                  % Daily Recommended Intake
                </Typography>
                {Object.entries(rda).map(([key, pct]) => (
                  <RdaBar key={key} label={key} percentage={pct} />
                ))}
              </>
            )}

            {/* Storage Instructions */}
            {recipe.storage_instructions && typeof recipe.storage_instructions === 'object' && Object.keys(recipe.storage_instructions).length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                  Storage Instructions
                </Typography>
                {Object.entries(recipe.storage_instructions).map(([key, val]) => (
                  <Typography key={key} variant="body2" mb={0.5}>
                    <strong>{key.replace(/_/g, ' ')}:</strong> {val}
                  </Typography>
                ))}
              </>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default RecipeDetailModal;
